import React, { useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayerProgress } from '../contexts/PlayerContext';
import { getTierColor, LEVEL_UNLOCKS } from '../constants/levelUnlocks';
import { motion, AnimatePresence } from 'framer-motion';
import './LevelUpOverlay.css';

const LevelUpOverlay = () => {
  const { t } = useTranslation('common');
  const { pendingLevelUp, clearLevelUp } = usePlayerProgress();

  // Auto-dismiss after 4s
  useEffect(() => {
    if (!pendingLevelUp) return;
    const timer = setTimeout(clearLevelUp, 4000);
    return () => clearTimeout(timer);
  }, [pendingLevelUp, clearLevelUp]);

  if (!pendingLevelUp) return null;

  const { newLevel, newUnlocks = [] } = pendingLevelUp;
  const tierColor = getTierColor(newLevel);

  // Map unlock IDs to translated game names
  const unlockNames = newUnlocks.map(id => {
    const unlock = LEVEL_UNLOCKS.find(u => u.unlockId === id);
    return unlock ? t(unlock.displayName) : id;
  });

  return (
    <AnimatePresence>
      <motion.div
        className="level-up-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={clearLevelUp}
      >
        <motion.div
          className="level-up-content"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 12, stiffness: 200 }}
        >
          <div className="level-up-title" style={{ color: tierColor }}>
            {t('level_up', { level: newLevel })}
          </div>

          {unlockNames.length > 0 && (
            <div className="level-up-unlocks">
              {unlockNames.map((name, i) => (
                <div key={i} className="level-up-unlock-item">
                  {t('unlocked', { name })}
                </div>
              ))}
            </div>
          )}

          <div className="level-up-dismiss">{t('click_to_dismiss')}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default memo(LevelUpOverlay);
